import { useEffect, useState } from "react";
import { fieldChecked, fieldValue, useModalOpen } from "../../app/components/polaris";
import type { FitmentFieldDataType, FitmentFieldDefinition, FitmentFieldSortDirection, FitmentFieldSortMode } from "../../app/models/FitmentCatalog";
import { getFieldSampleValues } from "../../app/utils/fitmentSampleValues";

export type FieldRoleOptionGroup = { title: string; options: { label: string; value: string; disabled?: boolean }[] };

type Props = {
    open: boolean;
    field?: FitmentFieldDefinition;
    role: string;
    roleOptions: FieldRoleOptionGroup[];
    requiredLocked: boolean;
    canRemove: boolean;
    onChange: (changes: Partial<FitmentFieldDefinition>) => void;
    onRoleChange: (role: string) => void;
    onClose: () => void;
    onRemove: () => void;
};

const valueTypes = [
    { label: "Text", value: "Text" }, { label: "Whole number", value: "Number" },
    { label: "Decimal", value: "Decimal" }, { label: "Date", value: "Date" },
];

// Field edits apply to the page draft; the page's save bar saves them.
const FitmentFieldEditor = ({ open, field, role, roleOptions, requiredLocked, canRemove, onChange, onRoleChange, onClose, onRemove }: Props) => {
    const [confirmRemove, setConfirmRemove] = useState(false);
    const modalRef = useModalOpen(open, onClose);
    useEffect(() => { if (!open) setConfirmRemove(false); }, [open]);
    const label = field?.label.trim() || "New field";

    return (
        <s-modal ref={modalRef} id="fitment-field-editor" heading={label}>
            {/* s-modal keeps its children mounted; render the form only while open. */}
            {open && field && <s-stack gap="base">
                {confirmRemove && <s-banner tone="warning" heading={`Remove ${label}?`}>
                    <s-stack direction="inline" gap="small-200">
                        <s-button tone="critical" onClick={onRemove}>Remove</s-button>
                        <s-button onClick={() => setConfirmRemove(false)}>Keep field</s-button>
                    </s-stack>
                </s-banner>}
                <s-select id="fitment-field-type" label="Field type" value={role} onChange={event => onRoleChange(fieldValue(event))}>
                    {roleOptions.map(group => <s-option-group key={group.title} label={group.title}>
                        {group.options.map(option => <s-option key={option.value} value={option.value} disabled={option.disabled}>{option.label}</s-option>)}
                    </s-option-group>)}
                </s-select>
                <s-text-field id="fitment-field-label" label="Display label" value={field.label} autocomplete="off"
                    onInput={event => onChange({ label: fieldValue(event) })} />
                {role === "custom" && <s-select label="Value type" value={field.dataType}
                    onChange={event => onChange({ dataType: fieldValue(event) as FitmentFieldDataType })}>
                    {valueTypes.map(option => <s-option key={option.value} value={option.value}>{option.label}</s-option>)}
                </s-select>}
                <s-divider />
                <s-heading>Required</s-heading>
                {requiredLocked ? <s-text color="subdued">Always required in fitment data and before search</s-text> : <>
                    <s-checkbox label="In fitment data" checked={field.isRequired}
                        onChange={event => onChange({ isRequired: fieldChecked(event) })} />
                    <s-checkbox label="Before customers can search" checked={field.isFilterable} disabled={!field.isRequired}
                        onChange={event => onChange({ isFilterable: fieldChecked(event) })} />
                </>}
                <s-divider />
                <s-checkbox label="Show in fitment table" checked={field.showInFitmentTable !== false}
                    onChange={event => onChange({ showInFitmentTable: fieldChecked(event) })} />
                <s-divider />
                <s-heading>Sorting</s-heading>
                <s-grid gridTemplateColumns="1fr 1fr" gap="small-200">
                    <s-select label="Sort mode" value={field.sortMode ?? "NaturalNumeric"}
                        onChange={event => onChange({ sortMode: fieldValue(event) as FitmentFieldSortMode })}>
                        <s-option value="NaturalNumeric">Natural numeric</s-option>
                        <s-option value="Alphabetical">Alphabetical</s-option>
                    </s-select>
                    <s-select label="Order" value={field.sortDirection ?? (role === "year" ? "Desc" : "Asc")}
                        onChange={event => onChange({ sortDirection: fieldValue(event) as FitmentFieldSortDirection })}>
                        <s-option value="Asc">Ascending</s-option>
                        <s-option value="Desc">Descending</s-option>
                    </s-select>
                </s-grid>
                <s-text color="subdued">{`Example: ${getFieldSampleValues(field).join(" → ")}`}</s-text>
            </s-stack>}
            <s-button slot="primary-action" variant="primary" onClick={onClose}>Done</s-button>
            <s-button slot="secondary-actions" tone="critical" disabled={!canRemove || confirmRemove}
                onClick={() => setConfirmRemove(true)}>Remove field</s-button>
        </s-modal>
    );
};

export default FitmentFieldEditor;
